import { svgIconDataUri } from "@loot-os/api-contract";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import {
  type IntercomState,
  intercomOf,
  noteRouteChange,
  onIntercomState,
  syncIntercom,
  toggleIntercom,
} from "../lib/intercom.js";
import { useShopper } from "../lib/shopper.js";
import { useTenant } from "../lib/tenant.js";

/**
 * The storefront's own chat button, bottom-right (above the tab bar on a phone), on the theme's
 * primary color.
 * Intercom's launcher is hidden so every store gets one launcher, themed and
 * carrying the tenant's uploaded icon (an `<img>` data URI, like the currency icon —
 * never inline markup) or the default glyph. Renders nothing while the
 * storefront has chat off, which is also when nothing Intercom is loaded.
 */
export function IntercomLauncher() {
  const { config } = useTenant();
  const { user, authPending } = useShopper();
  const pathname = useLocation({ select: (location) => location.pathname });
  const intercom = intercomOf(config);
  const appId = intercom.appId;
  const userId = user?.intercom?.userId ?? null;
  const userHash = user?.intercom?.userHash ?? null;
  const email = user?.email ?? null;
  const [state, setState] = useState<IntercomState>({ unread: 0, open: false });

  // One boot per page load, after the session check settles — a visit that booted anonymous and
  // re-booted identified a moment later would make every shopper a lead first. Sign-out and
  // session loss change the identity and re-boot; syncIntercom is a no-op for anything else.
  useEffect(() => {
    if (!appId || authPending) return;
    syncIntercom({
      appId,
      company: { id: config.tenantId, name: config.name },
      attributes: { storefront_hostname: window.location.hostname, storefront_mode: config.mode },
      identity: userId && userHash && email ? { userId, userHash, email } : null,
    });
  }, [appId, authPending, userId, userHash, email, config.tenantId, config.name, config.mode]);

  // Chat switched off under an open page (a config refetch): end the session.
  useEffect(() => {
    if (!appId) syncIntercom(null);
  }, [appId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is a trigger, not an input — every SPA navigation pings Intercom for new messages, though the path itself is not read.
  useEffect(() => {
    if (appId) noteRouteChange();
  }, [appId, pathname]);

  useEffect(() => onIntercomState(setState), []);

  if (!appId) return null;

  return (
    <button
      type="button"
      aria-label={state.open ? "Close support chat" : "Open support chat"}
      aria-expanded={state.open}
      onClick={toggleIntercom}
      className="fixed right-4 bottom-[5.125rem] z-20 flex size-12 items-center justify-center rounded-full md:bottom-4 bg-accent-default text-text-on-accent shadow-float ring-1 ring-border-subtle transition-transform duration-fast ease-standard hover:scale-105 active:scale-95 motion-reduce:transition-none"
    >
      {intercom.launcherIconSvg ? (
        <img src={svgIconDataUri(intercom.launcherIconSvg)} alt="" className="size-6" />
      ) : (
        <DefaultChatIcon />
      )}
      {state.unread > 0 && (
        <span className="absolute -top-1 -right-1 min-w-5 rounded-pill bg-status-danger px-1 text-center font-body text-body-b3-strong leading-5 text-text-on-accent">
          {state.unread > 9 ? "9+" : state.unread}
        </span>
      )}
    </button>
  );
}

/** A speech bubble in the button's foreground color, for stores that upload no icon. */
function DefaultChatIcon() {
  return (
    <svg viewBox="0 0 28 28" fill="none" aria-hidden="true" className="size-6">
      <title>Support chat</title>
      <path
        d="M23.3333 3.5H4.66667C3.37983 3.5 2.33333 4.5465 2.33333 5.83333V19.8333C2.33333 21.1202 3.37983 22.1667 4.66667 22.1667H7V25.0833C7 25.4053 7.26133 25.6667 7.58333 25.6667C7.67783 25.6667 9.84433 25.6247 11.151 22.1667H23.3333C24.6202 22.1667 25.6667 21.1202 25.6667 19.8333V5.83333C25.6667 4.5465 24.6202 3.5 23.3333 3.5Z"
        fill="currentColor"
      />
    </svg>
  );
}
